import { useMutation } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';

import type { AiRunResult } from '@mtgcraft/shared';

import { functions } from '@/services/firebase';

import useElapsedTimer from './useElapsedTimer';

export interface UseAiFeatureResult<TInput, TResult> {
  execute: (input: TInput) => void;
  reset: () => void;
  isRunning: boolean;
  /** Ticks while running, then holds the final duration */
  elapsedMs: number;
  run: AiRunResult<TResult> | null;
  error: string | null;
}

function toMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Something went wrong running this AI feature.';
}

/**
 * Generic driver for an AI callable: tracks elapsed time, normalises errors, and surfaces
 * non-success run statuses (`invalid_output`, `failed`) as errors rather than results.
 */
export default function useAiFeature<TInput, TResult>(callableName: string): UseAiFeatureResult<TInput, TResult> {
  const [run, setRun] = useState<AiRunResult<TResult> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (input: TInput) => {
      const callable = httpsCallable<TInput, AiRunResult<TResult>>(functions, callableName);
      const response = await callable(input);
      return response.data;
    },
    onSuccess: (result) => {
      setRun(result);
      setError(result.status === 'succeeded' ? null : (result.error?.message ?? 'The model returned an unusable response.'));
    },
    onError: (caught) => {
      setRun(null);
      setError(toMessage(caught));
    },
  });

  const elapsedMs = useElapsedTimer(mutation.isPending);
  const finalElapsedMs = mutation.isPending ? elapsedMs : (run?.durationMs ?? elapsedMs);

  const execute = useCallback(
    (input: TInput) => {
      setError(null);
      setRun(null);
      mutation.mutate(input);
    },
    [mutation],
  );

  const reset = useCallback(() => {
    setRun(null);
    setError(null);
    mutation.reset();
  }, [mutation]);

  return {
    execute,
    reset,
    isRunning: mutation.isPending,
    elapsedMs: finalElapsedMs,
    run,
    error,
  };
}
